// Sort options for the LOCAL-lens MarketFeedRow feed (real markets).
//
// Architect-approved 2026-05-20 across five decisions; brief at
// docs/audit/2026-05-20-market-feed-sort-brief.md. Three criteria at
// launch — Nearest closure (default), Biggest pool, Newest market —
// with the architecture deliberately shaped so further criteria
// (most-divergent / Penumbra, alphabetical, by-lens) are one entry
// here, not a new code path.
//
// Decision 4 — settled markets always sink below active markets as a
// group — lives in `partitionAndSort` as a partition step BEFORE sort,
// not inside each comparator. This means a future criterion added via
// a new FEED_SORT_OPTIONS entry inherits the settled-sink rule
// automatically and cannot forget it.

export type FeedSortKey = 'closure' | 'pool' | 'newest';

export interface FeedSortOption {
  value: FeedSortKey;
  label: string;
}

export const FEED_SORT_OPTIONS: readonly FeedSortOption[] = [
  { value: 'closure', label: 'Nearest closure' },
  { value: 'pool',    label: 'Biggest pool'   },
  { value: 'newest',  label: 'Newest market'  },
] as const;

export const FEED_SORT_DEFAULT: FeedSortKey = 'closure';

// The minimal shape the partition+sort step needs to see. Callers
// (currently MultiPage.tsx) enrich their fetched markets with the
// fields below and pass them in.
export interface FeedSortableMarket {
  settled:              boolean;
  challenge_deadline?:  string | null;
  created_at?:          string | null;
  settled_at?:          string | null;
  ocVolume:             number;
}

function timeMs(iso?: string | null): number {
  if (!iso) return NaN;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : NaN;
}

// NaN-tolerant ascending compare — missing values sort last (toward
// bottom of the active group). The cockpit's seed markets always have
// the relevant timestamps so this is defensive, not load-bearing.
function ascNum(a: number, b: number): number {
  const aN = Number.isNaN(a);
  const bN = Number.isNaN(b);
  if (aN && bN) return 0;
  if (aN) return  1;
  if (bN) return -1;
  return a - b;
}

function descNum(a: number, b: number): number {
  return ascNum(b, a);
}

function activeComparator(
  sortKey: FeedSortKey,
): (a: FeedSortableMarket, b: FeedSortableMarket) => number {
  switch (sortKey) {
    case 'closure':
      return (a, b) => ascNum(timeMs(a.challenge_deadline), timeMs(b.challenge_deadline));
    case 'pool':
      return (a, b) => descNum(a.ocVolume, b.ocVolume);
    case 'newest':
      return (a, b) => descNum(timeMs(a.created_at), timeMs(b.created_at));
  }
}

function settledComparator(a: FeedSortableMarket, b: FeedSortableMarket): number {
  // Most-recently-settled first within the sunk group.
  return descNum(timeMs(a.settled_at), timeMs(b.settled_at));
}

export function partitionAndSort<T extends FeedSortableMarket>(
  markets: readonly T[],
  sortKey: FeedSortKey,
): T[] {
  const active:  T[] = [];
  const settled: T[] = [];
  for (const m of markets) (m.settled ? settled : active).push(m);
  active.sort(activeComparator(sortKey));
  settled.sort(settledComparator);
  return [...active, ...settled];
}
